import { type FC, type ReactNode, useRef, useState } from "react";

import { ArrowRight, Check, ChevronDown, Clock, Lock } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/utils/core";

import type { SettleStepStatus } from "../settleSteps";

interface SettleStepRowProps {
  stepNumber: number;
  title: string;
  status: SettleStepStatus;
  description?: string;
  cta?: { label: string; onClick: () => void };
  secondaryLink?: { label: string; onClick?: () => void };
  waitingLabel?: string;
  waitingDescription?: string;
  expandContent?: ReactNode;
  alwaysShowContent?: boolean;
  children?: ReactNode;
}

export const SettleStepRow: FC<SettleStepRowProps> = ({
  stepNumber,
  title,
  status,
  description,
  cta,
  secondaryLink,
  waitingLabel,
  waitingDescription,
  expandContent,
  alwaysShowContent = false,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(alwaysShowContent);
  const contentRef = useRef<HTMLDivElement>(null);
  const canExpand = (status === "upcoming" || alwaysShowContent) && !!(expandContent || description);

  if (status === "active") {
    return (
      <div className="px-2 py-2 sm:px-4">
        <div className="flex flex-col gap-4 rounded-lg border border-brand bg-brand-subtle p-3 sm:p-4">
          <div className="flex items-start gap-3 sm:gap-4">
            <NumberBadge number={stepNumber} filled className="hidden sm:flex" />
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold">{title}</p>
                {description ? <p className="text-xs text-secondary">{description}</p> : null}
              </div>
              {children}
              {cta || secondaryLink ? (
                <div className="flex items-center gap-3">
                  {cta ? (
                    <Button variant="brand" onClick={cta.onClick}>
                      {cta.label}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  ) : null}
                  {secondaryLink ? (
                    <button
                      onClick={secondaryLink.onClick}
                      className="text-xs text-secondary"
                    >
                      {secondaryLink.label}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === "complete") {
    return (
      <>
        <div className="flex items-center gap-4 px-4 py-4 sm:px-8">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand">
            <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />
          </div>
          <p className="flex-1 text-sm font-medium text-tertiary">{title}</p>
          {secondaryLink ? (
            <Button
              variant="link"
              size={null}
              onClick={secondaryLink.onClick}
              className="shrink-0 text-sm font-medium text-brand underline"
            >
              {secondaryLink.label}
            </Button>
          ) : null}
        </div>
      </>
    );
  }

  if (status === "waiting") {
    return (
      <div className="px-2 pb-2 sm:px-4">
        <div className="flex flex-col gap-4 rounded-lg border border-primary bg-subtle p-3 sm:p-4">
          <div className="flex items-start gap-4">
            <Clock className="h-6 w-6 shrink-0 text-secondary" />
            <div className="flex flex-1 flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="flex items-start gap-1">
                  <p className="flex-1 text-sm font-semibold">{title}</p>
                  {waitingLabel ? (
                    <span className="shrink-0 rounded-full border border-yellow-300 bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-warning">
                      {waitingLabel}
                    </span>
                  ) : null}
                </div>
                {waitingDescription ? (
                  <p className="text-xs text-secondary">{waitingDescription}</p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === "locked" && !alwaysShowContent) {
    return (
      <>
        <div className="flex items-center gap-4 px-4 py-4 sm:px-8">
          <NumberBadge number={stepNumber} />
          <p className="flex-1 text-sm font-medium">{title}</p>
          <Lock className="h-4 w-4 text-tertiary" />
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className={cn("flex items-start gap-4 px-4 py-4 sm:px-8", canExpand && "cursor-pointer")}
        role={canExpand ? "button" : undefined}
        tabIndex={canExpand ? 0 : undefined}
        onClick={canExpand ? () => setIsExpanded((prev) => !prev) : undefined}
        onKeyDown={canExpand ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsExpanded((prev) => !prev); } } : undefined}
      >
        <NumberBadge number={stepNumber} />
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <p className="text-sm font-medium">{title}</p>
          <div
            ref={contentRef}
            className="grid transition-[grid-template-rows] duration-250 ease-in-out"
            style={{ gridTemplateRows: isExpanded ? "1fr" : "0fr" }}
          >
            <div className="overflow-hidden">
              <div className="flex flex-col gap-3">
                {description ? <p className="text-sm text-secondary">{description}</p> : null}
                {expandContent}
              </div>
            </div>
          </div>
        </div>
        {canExpand ? (
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-tertiary transition-transform duration-200",
              isExpanded && "rotate-180",
            )}
          />
        ) : null}
      </div>
    </>
  );
};

const NumberBadge: FC<{ number: number; filled?: boolean; className?: string }> = ({ number, filled = false, className }) => (
  <div
    className={cn(
      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-medium",
      filled
        ? "bg-brand text-white"
        : "border border-primary bg-white text-primary",
      className,
    )}
  >
    {number}
  </div>
);
